import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RefreshCw, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const ROL = { gran_admin: "Gran admin", coordinador: "Coordinador", franquiciatario: "Franquiciatario", gerente_propia: "Gerente propia", gerente_inversionista: "Gerente inversionista", gerente_franquicia: "Gerente franquicia", ejecutiva: "Ejecutiva" };

export default function DiagnosticoCajas({ sucursales, onCambio }) {
  const [d, setD] = useState(null);
  const [fix, setFix] = useState({});
  const [saving, setSaving] = useState("");
  const [soloProblemas, setSoloProblemas] = useState(true);

  const cargar = useCallback(() => { api.get("/cajas/diagnostico").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function asignar(u) {
    const sid = fix[u.id];
    if (!sid) return toast.error("Elige la sucursal correcta");
    setSaving(u.id);
    try {
      const s = sucursales.find((x) => x.id === sid);
      await api.put(`/usuarios/${u.id}`, { sucursal_id: sid, razon_social_id: s?.razon_social_id || null });
      toast.success(`${u.nombre} ahora opera la caja de ${s?.nombre}`);
      cargar(); onCambio?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(""); }
  }

  if (!d) return null;
  const lista = soloProblemas ? d.usuarios.filter((u) => u.problema) : d.usuarios;
  return (
    <div className="surface-card p-5 mb-6" data-testid="diagnostico-cajas">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="eyebrow mb-1">Diagnóstico de cajas por usuario</div>
          <p className="text-xs text-brand-muted">Cada sucursal tiene sus propias cajas 01/02/03. Un usuario sin sucursal (o con una sucursal borrada) no puede abrir caja{d.almacen ? ` — antes caía en ${d.almacen.nombre}` : ""}.</p>
        </div>
        <div className="flex items-center gap-2">
          {d.con_problema ? <span data-testid="diag-problemas" className="chip h-7 px-2.5 text-xs font-bold text-red-700 bg-red-50 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {d.con_problema} por corregir</span> : <span data-testid="diag-ok" className="chip h-7 px-2.5 text-xs font-bold text-emerald-700 bg-emerald-50 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Todos con sucursal</span>}
          <button data-testid="diag-toggle" onClick={() => setSoloProblemas(!soloProblemas)} className="btn-soft h-8 px-3 text-xs">{soloProblemas ? "Ver todos" : "Solo problemas"}</button>
          <button data-testid="diag-refresh" onClick={cargar} className="p-2 text-brand-muted hover:text-brand-ink"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>
      {lista.length === 0 ? <p className="text-sm text-brand-muted">Sin usuarios que corregir.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-brand-muted"><th className="py-2 pr-3">Usuario</th><th className="py-2 pr-3">Rol</th><th className="py-2 pr-3">Sucursal de su caja</th><th className="py-2 pr-3">Cajas abiertas ahí</th><th className="py-2"></th></tr></thead>
          <tbody>
            {lista.map((u) => (
              <tr key={u.id} data-testid={`diag-user-${u.email}`} className={`border-t border-brand-line/60 ${u.problema ? "bg-red-50/60" : ""}`}>
                <td className="py-2 pr-3"><div className="font-semibold text-brand-ink">{u.nombre}</div><div className="text-[11px] text-brand-muted">{u.email}</div></td>
                <td className="py-2 pr-3 text-xs">{ROL[u.rol] || u.rol}</td>
                <td className="py-2 pr-3 text-xs">{u.elige_sucursal ? <span className="text-brand-muted">Elige sucursal en el POS</span> : u.problema === "sin_sucursal" ? <span className="font-bold text-red-700">SIN SUCURSAL</span> : u.problema === "sucursal_inexistente" ? <span className="font-bold text-red-700">Sucursal borrada</span> : <span className="font-semibold">{u.sucursal_nombre}</span>}</td>
                <td className="py-2 pr-3 text-[11px] text-brand-slate">{u.cajas_abiertas.length ? u.cajas_abiertas.map((c) => `${c.caja} (${c.abierto_por})`).join(", ") : "ninguna"}</td>
                <td className="py-2 text-right">{!u.elige_sucursal && (
                  <div className="flex items-center gap-1.5 justify-end">
                    <select data-testid={`diag-fix-select-${u.email}`} value={fix[u.id] || ""} onChange={(e) => setFix({ ...fix, [u.id]: e.target.value })} className="field h-8 w-52 text-xs"><option value="">{u.problema ? "Asignar sucursal…" : "Cambiar sucursal…"}</option>{sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}</select>
                    <button data-testid={`diag-fix-btn-${u.email}`} disabled={!fix[u.id] || saving === u.id} onClick={() => asignar(u)} className="btn-ink h-8 px-3 text-xs disabled:opacity-40">{saving === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Guardar"}</button>
                  </div>)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
